import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ai_powered_cv_logo from "../../../assets/img/Ai-powered-cv-logo.png";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Importing the eye icons
import "./Login.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password should be at least 6 characters")
    .required("Password is required"),
});

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const notifySuccess = () => toast.success("Login Successful");

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const response = await axios.post(`${BASE_URL}/api/users/login`, values);

        // Store the received token in localStorage
        localStorage.setItem("token", response.data.token);

        notifySuccess();
        setTimeout(() => navigate("/dashboard"), 1000);
        resetForm();
      } catch (error) {
        toast.error(error.response?.data?.message || "Server down, please try again later");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="login-container">
      <Container className="d-flex flex-column align-items-center justify-content-center">
        <h1 className="text-white fw-bold mb-4">
          <Link to="/" className="text-decoration-none d-flex justify-content-center me-2 ">
            <img 
              src={ai_powered_cv_logo}
              alt="AI-Powered CV" 
              className="logo-img w-50"
            />
          </Link>
        </h1>

        <Row className="justify-content-center w-100">
          <Col md={6} lg={5}>
            <Card className="p-4 glassmorphism">
              <Card.Body>
                <h2 className="text-center mb-4 text-white">Login</h2>
                <Form onSubmit={formik.handleSubmit}>
                  {/* Email Input */}
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      {...formik.getFieldProps("email")}
                      isInvalid={formik.touched.email && formik.errors.email}
                      className="p-2"
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Password Input */}
                  <Form.Group className="mb-3">
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Password"
                        {...formik.getFieldProps("password")}
                        isInvalid={formik.touched.password && formik.errors.password}
                        className="p-2"
                      />
                      <Button
                        type="button"
                        className="show-hide-btn"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />} {/* Eye icons based on showPassword state */}
                      </Button>
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Submit Button */}
                  <Button type="submit" className="w-100 btn-custom" disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? <Spinner animation="border" size="sm" /> : "Login"}
                  </Button>

                  {/* Forgot Password */}
                  <div className="text-center mt-3">
                    <Link to="/forget-password" className="text-white">
                      Forgot Password?
                    </Link>
                  </div>

                  {/* Signup Link */}
                  <p className="text-center text-white mt-3">
                    New User?{" "}
                    <Link to="/signup" className="text-primary fw-bold">
                      Sign up here
                    </Link>
                  </p>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer />
    </div>
  );
}

export default Login;
